import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Calendar, Eye, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import ArticleCard from '../components/ArticleCard';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AuthorPage = () => {
  const { authorName } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuthorProfile();
  }, [authorName]);

  const loadAuthorProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/author/${authorName}`);
      setAuthor(response.data.author);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error loading author:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">Loading author profile...</p>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">Author Not Found</h2>
        <p className="text-gray-600 mb-6">We couldn't find this author.</p>
        <Link to="/" className="text-blue-600 hover:underline">
          Return to Home
        </Link>
      </div>
    );
  }

  const profilePictureUrl = author.profile_picture 
    ? `${API_BASE_URL}${author.profile_picture}` 
    : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Author Header */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Profile Picture */}
            <div className="flex-shrink-0">
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt={author.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <User size={48} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* Author Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-serif font-bold mb-2">{author.name}</h1>
              <p className="text-gray-600 mb-4">
                <Calendar size={16} className="inline mr-2" />
                Member since {new Date(author.member_since).toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>

              {author.bio && (
                <p className="text-gray-700 mb-4 leading-relaxed">{author.bio}</p>
              )}

              {/* Stats */}
              <div className="flex gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{articles.length}</div>
                    <div className="text-xs text-gray-500">Articles</div>
                  </div>
                </div>
                
                {/* Add more stats as needed */}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Articles by Author */}
      <div className="mb-6">
        <h2 className="text-2xl font-serif font-bold mb-6">
          Articles by {author.name}
        </h2>
      </div>

      {articles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <ArticleCard
              key={article.id || article._id}
              id={article.id || article._id}
              title={article.title}
              excerpt={article.excerpt}
              image={article.image}
              category={article.category}
              publishedDate={article.published_date}
              author={article.author}
              slug={article.slug}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">No articles published yet</p>
        </div>
      )}
    </div>
  );
};

export default AuthorPage;
